import axios from "axios";
import _ from "lodash";
import constants from "@/config/constants";

function initialState() {
    return {
        item: {
            event_id: null,
            module_id: null,
            module_name: null,
            keywords: []
        },
        existingKeywords: [],
        loading: false,
        saved: false,
        error: null
    }
}

const getters = {
    item: state => state.item,
    loading: state => state.loading,
    saved: state => state.saved,
    error: state => state.error,
    existingKeywords: state => state.existingKeywords,
    keywords: state => state.item.keywords
}

const actions = {
    setItem({ commit }, value) {
        commit('setItem', value);
    },
    
    setKeywords({ commit }, keywords) {
        commit('setKeywords', keywords);
    },
    
    /**
     * Fetch match making data for a specific module item
     */
    async fetchMatchMaking({ commit, state }, { eventId, moduleId, moduleName }) {
        commit('setLoading', true);
        commit('setError', null);
        
        const headers = {
            'Authorization': 'Bearer ' + localStorage.getItem('token')
        };
        
        try {
            const response = await axios.get(
                `${constants.API_URL}/api/match-making/${eventId}/${moduleName}/${moduleId}`,
                { headers }
            );
            
            const data = _.get(response, 'data.data', {});
            const keywords = data.keywords || [];
            
            commit('setItem', {
                event_id: eventId,
                module_id: moduleId,
                module_name: moduleName,
                keywords: keywords
            });
            
            commit('setExistingKeywords', keywords);
            return response;
        } catch (error) {
            // If 404, it's just not created yet, that's fine
            if (error.response && error.response.status !== 404) {
                commit('setError', error.response?.data?.message || 'Failed to fetch match making data');
                console.error('Error fetching match making:', error);
            } else {
                // Reset to empty state for new items
                commit('setItem', {
                    event_id: eventId,
                    module_id: moduleId,
                    module_name: moduleName,
                    keywords: []
                });
                commit('setExistingKeywords', []);
            }
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },
    
    /**
     * Create or update match making
     */
    async saveMatchMaking({ commit, state }, { eventId, moduleId, moduleName, keywords }) {
        commit('setLoading', true);
        commit('setError', null);
        commit('setSaved', false);
        
        const headers = {
            'Authorization': 'Bearer ' + localStorage.getItem('token')
        };
        
        const params = {
            event_id: eventId,
            module_id: moduleId,
            module_name: moduleName,
            keywords: keywords || state.item.keywords
        };
        
        try {
            const response = await axios.post(
                `${constants.API_URL}/api/match-making`,
                params,
                { headers }
            );
            
            const data = _.get(response, 'data.data', {});
            const savedKeywords = _.get(data, `${moduleName.toLowerCase().replace(/-/g, '_')}s`, []);
            
            // Extract keywords from the saved data
            let finalKeywords = keywords || state.item.keywords;
            if (savedKeywords && savedKeywords.length) {
                const foundItem = savedKeywords.find(item => 
                    item[`${moduleName.toLowerCase().replace(/-/g, '_')}_id`]?.toString() === moduleId
                );
                if (foundItem) {
                    finalKeywords = foundItem.keywords || [];
                }
            }
            
            commit('setItem', {
                event_id: eventId,
                module_id: moduleId,
                module_name: moduleName,
                keywords: finalKeywords
            });
            
            commit('setExistingKeywords', finalKeywords);
            commit('setSaved', true);
            
            return response;
        } catch (error) {
            commit('setError', error.response?.data?.message || 'Failed to save match making data');
            console.error('Error saving match making:', error);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },
    
    /**
     * Update match making (PATCH)
     */
    async updateMatchMaking({ commit, state }, { eventId, moduleId, moduleName, keywords }) {
        commit('setLoading', true);
        commit('setError', null);
        
        const headers = {
            'Authorization': 'Bearer ' + localStorage.getItem('token')
        };
        
        const params = {
            event_id: eventId,
            module_id: moduleId,
            module_name: moduleName,
            keywords: keywords || state.item.keywords
        };
        
        try {
            const response = await axios.patch(
                `${constants.API_URL}/api/matchmaking`,
                params,
                { headers }
            );
            
            const data = _.get(response, 'data.data', {});
            commit('setItem', {
                event_id: eventId,
                module_id: moduleId,
                module_name: moduleName,
                keywords: keywords || state.item.keywords
            });
            
            commit('setExistingKeywords', keywords || state.item.keywords);
            return response;
        } catch (error) {
            commit('setError', error.response?.data?.message || 'Failed to update match making data');
            console.error('Error updating match making:', error);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },
    
    /**
     * Delete match making item
     */
    async deleteMatchMaking({ commit, state }, { eventId, moduleName, moduleId }) {
        commit('setLoading', true);
        commit('setError', null);
        
        const headers = {
            'Authorization': 'Bearer ' + localStorage.getItem('token')
        };
        
        try {
            const response = await axios.delete(
                `${constants.API_URL}/api/matchmaking/${eventId}/${moduleName}/${moduleId}`,
                { headers }
            );
            
            commit('resetState');
            return response;
        } catch (error) {
            commit('setError', error.response?.data?.message || 'Failed to delete match making data');
            console.error('Error deleting match making:', error);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },
    
    /**
     * Add keywords to existing item
     */
    async addKeywords({ dispatch, state }, { eventId, moduleId, moduleName, newKeywords }) {
        const currentKeywords = state.item.keywords;
        const keywordsToAdd = Array.isArray(newKeywords) ? newKeywords : [newKeywords];
        const updatedKeywords = [...new Set([...currentKeywords, ...keywordsToAdd])];
        
        return await dispatch('saveMatchMaking', {
            eventId,
            moduleId,
            moduleName,
            keywords: updatedKeywords
        });
    },
    
    /**
     * Remove keywords from item
     */
    async removeKeywords({ dispatch, state }, { eventId, moduleId, moduleName, keywordsToRemove }) {
        const currentKeywords = state.item.keywords;
        const keywordsToDelete = Array.isArray(keywordsToRemove) ? keywordsToRemove : [keywordsToRemove];
        const updatedKeywords = currentKeywords.filter(k => !keywordsToDelete.includes(k));
        
        return await dispatch('saveMatchMaking', {
            eventId,
            moduleId,
            moduleName,
            keywords: updatedKeywords
        });
    },
    
    resetState({ commit }) {
        commit('resetState');
    },
    
    setLoading({ commit }, loading) {
        commit('setLoading', loading);
    },
    
    setError({ commit }, error) {
        commit('setError', error);
    }
}

const mutations = {
    setItem(state, item) {
        state.item = { ...state.item, ...item };
    },
    
    setKeywords(state, keywords) {
        state.item.keywords = keywords;
        state.existingKeywords = keywords;
    },
    
    setExistingKeywords(state, keywords) {
        state.existingKeywords = keywords;
    },
    
    setLoading(state, loading) {
        state.loading = loading;
    },
    
    setSaved(state, saved) {
        state.saved = saved;
    },
    
    setError(state, error) {
        state.error = error;
    },
    
    resetState(state) {
        const initial = initialState();
        state.item = initial.item;
        state.existingKeywords = initial.existingKeywords;
        state.loading = initial.loading;
        state.saved = initial.saved;
        state.error = initial.error;
    }
}

export default {
    namespaced: true,
    state: initialState,
    getters,
    actions,
    mutations
}